import type { ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Crown, Gift, Sparkles, Zap } from 'lucide-react';
import { formatNumber } from '@xdocs/i18n';
import { Badge, Button, Card, cn, useI18n } from '@xdocs/ui';
import { useAuth } from '../../../auth/AuthProvider.js';
import { BUY_CREDITS_SECTION_ID, BuyCreditsGrid } from '../../settings/billing/BuyCreditsGrid.js';
import { COST_ROWS } from '../../settings/billing/creditCostRows.js';
import type { SettingsBillingMessageId } from '../../settings/billing/useSettingsBillingT.js';
import { useSettingsBillingT } from '../../settings/billing/useSettingsBillingT.js';
import styles from './CreditsExplainerPage.module.css';

/**
 * Mirrors of server-side constants — NOT importable client-side, keep in sync:
 * - SIGNUP_GRANT_CREDITS  → apps/server/src/auth/auth-service.ts (100)
 * - SHARE_PUBLIC_REWARD_CREDITS → apps/server/src/moderation/handlers.ts (5)
 * - REFERRAL_REWARD_CREDITS → packages/core/src/referral-rewards.ts (10).
 *   `@xdocs/core` re-exports it, but that module imports `@xdocs/db`/drizzle
 *   (server-only), so pulling it into the web bundle would drag the DB layer
 *   in — hence the literal here too.
 */
const SIGNUP_GRANT_CREDITS = 100;
const SHARE_PUBLIC_REWARD_CREDITS = 5;
const REFERRAL_REWARD_CREDITS = 10;

/** "How the credits system works" 2×2 grid — landing FeaturesGrid visual language. */
const EXPLAINER_CARDS = [
  { key: 'start', icon: Sparkles, vars: { count: SIGNUP_GRANT_CREDITS } },
  { key: 'spend', icon: Zap, vars: undefined },
  {
    key: 'earn',
    icon: Gift,
    vars: { shareReward: SHARE_PUBLIC_REWARD_CREDITS, referralReward: REFERRAL_REWARD_CREDITS },
  },
  { key: 'pro', icon: Crown, vars: undefined },
] as const;

/**
 * `/pricing/credits` (F48) — the standalone credits explainer page
 * (design/app/33-credits-info.html), PUBLIC (outside `RequireAuth`, no
 * `AppShellLayout`), so it carries its own landing-style chrome (user
 * 2026-07-04: the bare flush-left version "looks bad, doesn't show the
 * landing page structure"):
 * - header/footer visually match apps/landing's SiteHeader/SiteFooter
 *   (logo → https://xdocs.app; auth-aware actions: Log in + Start free when
 *   anonymous, a single Open app CTA when authed);
 * - main content sits in the landing `min(1130px, 92vw)` container;
 * - a "how the credits system works" 2×2 card grid (real system numbers,
 *   see constants above) between the description and the cost table.
 *
 * Deliberate reuse (single sources, zero drift):
 * - Cost rows: `creditCostRows.ts` — the SAME matrix `CreditsInfoModal`
 *   renders on `/settings/billing` (see that file's doc comment for why the
 *   matrix is mockup-verbatim rather than derived from the server's
 *   `action_credit_costs` table).
 * - Copy: the `settingsBilling:creditsInfo.*` catalog keys (EN+RO) —
 *   mockup-33's copy is byte-identical between the modal and this page, so
 *   a second `pricingCredits:` scope would just duplicate every string.
 * - Packs + checkout: `BuyCreditsGrid` (F57 pack cards + promo field +
 *   `billing.checkout.start` Paddle launch) mounted below the fold — its
 *   own WIRING.md §3 blesses exactly this reuse. The mockup's single
 *   **Buy credits** button hands off to it (scroll), mirroring
 *   `CreditsSection`'s modal → grid handoff on `/settings/billing`.
 */
export function CreditsExplainerPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();
  // PUBLIC route (user 2026-07-04: "/pricing/credits shouldn't be auth
  // gated") — the explainer table is marketing-adjacent content; only the
  // checkout grid needs a session, so logged-out visitors get a login CTA
  // in its place (returning here after login via location state).
  const { status } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = status === 'authenticated';
  const year = new Date().getFullYear();

  function handleBuyCredits(): void {
    document.getElementById(BUY_CREDITS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className={cn(styles['page'])}>
      <header className={cn(styles['header'])}>
        <div className={cn(styles['headerInner'])}>
          {/* Marketing site logo — plain <a>, the wordmark leaves the SPA. */}
          <a href="https://xdocs.app" className={cn(styles['mark'])} aria-label="xDocs">
            <span className={cn(styles['markX'])}>{/* i18n-exempt: brand name ("xDocs"), split across two spans for styling */}x</span>
            <span>{/* i18n-exempt: brand name ("xDocs"), split across two spans for styling */}Docs</span>
            <span className={cn(styles['markDot'])} aria-hidden="true" />
          </a>
          <div className={cn(styles['headerActions'])}>
            {isAuthenticated ? (
              <Link to="/" className={cn(styles['headerCta'])}>
                {t('settingsBilling:creditsInfo.header.openApp')}
              </Link>
            ) : (
              <>
                <Link to="/login" className={cn(styles['headerLogin'])}>
                  {t('settingsBilling:creditsInfo.header.login')}
                </Link>
                <Link to="/signup" className={cn(styles['headerCta'])}>
                  {t('settingsBilling:creditsInfo.header.startFree')}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={cn(styles['container'])}>
        <p className={cn(styles['eyebrow'])}>{t('settingsBilling:creditsInfo.eyebrow')}</p>
        <h1 className={cn(styles['title'])}>{t('settingsBilling:creditsInfo.title')}</h1>
        <p className={cn(styles['description'])}>{t('settingsBilling:creditsInfo.description')}</p>

        {/* How the credits system works — grounded in the real system values
            (the ICU vars mirror the server constants documented above). */}
        <ul className={cn(styles['explainerGrid'])}>
          {EXPLAINER_CARDS.map((card) => {
            const Icon = card.icon;
            const titleId: SettingsBillingMessageId = `settingsBilling:creditsInfo.explainer.${card.key}.title`;
            const bodyId: SettingsBillingMessageId = `settingsBilling:creditsInfo.explainer.${card.key}.body`;
            return (
              <li key={card.key} className={cn(styles['explainerCard'])}>
                <span className={cn(styles['explainerIcon'])} aria-hidden="true">
                  <Icon size={20} />
                </span>
                <h2 className={cn(styles['explainerTitle'])}>{t(titleId, card.vars)}</h2>
                <p className={cn(styles['explainerBody'])}>{t(bodyId, card.vars)}</p>
              </li>
            );
          })}
        </ul>

        <div className={cn(styles['tableCard'])}>
          <table className={cn(styles['table'])}>
            <thead>
              <tr>
                <th scope="col" className={cn(styles['actionCol'])}>
                  {t('settingsBilling:creditsInfo.column.action')}
                </th>
                <th scope="col" className={cn(styles['numCol'])}>
                  {t('settingsBilling:creditsInfo.column.standard')}
                </th>
                <th scope="col" className={cn(styles['numCol'])}>
                  {t('settingsBilling:creditsInfo.column.advanced')}
                </th>
                <th scope="col" className={cn(styles['numCol'])}>
                  {t('settingsBilling:creditsInfo.column.mastery')}
                </th>
              </tr>
            </thead>
            <tbody>
              {COST_ROWS.map((row) => (
                <tr key={row.nameKey}>
                  <th scope="row">
                    <span className={cn(styles['actionName'])}>
                      {t(`settingsBilling:${row.nameKey}`)}
                      {row.pro && <Badge tone="pro">{t('settingsBilling:creditsInfo.badge.pro')}</Badge>}
                    </span>
                  </th>
                  <td>{formatNumber(locale, row.standard)}</td>
                  <td>{formatNumber(locale, row.advanced)}</td>
                  <td>{formatNumber(locale, row.mastery)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={cn(styles['buyRow'])}>
          <Button variant="primary" onClick={handleBuyCredits}>
            {t('settingsBilling:creditsInfo.buy')}
          </Button>
        </div>

        {isAuthenticated ? (
          /* F57 packs strip — owns its own loading skeleton / error+retry /
             empty / populated states (and the promo field + Paddle checkout
             launch), identical to /settings/billing's Buy-credits section. */
          <BuyCreditsGrid />
        ) : (
          /* Logged-out: checkout needs a session (`billing.checkout.start`
             is an authed op) — same section anchor so the Buy-credits CTA's
             scroll handoff still lands here. */
          <Card id={BUY_CREDITS_SECTION_ID} className={cn(styles['loginToBuy'])}>
            <h2 className={cn(styles['loginToBuyTitle'])}>
              {t('settingsBilling:creditsInfo.loginToBuy.title')}
            </h2>
            <p className={cn(styles['loginToBuyDescription'])}>
              {t('settingsBilling:creditsInfo.loginToBuy.description')}
            </p>
            <Button
              variant="primary"
              onClick={() => {
                // Same location-state contract LoginPage's post-login redirect
                // reads (`from.pathname`) — lands the visitor back here.
                void navigate('/login', { state: { from: { pathname: location.pathname } } });
              }}
            >
              {t('settingsBilling:creditsInfo.loginToBuy.cta')}
            </Button>
          </Card>
        )}
      </main>

      {/* Marketing footer — apps/landing SiteFooter is the visual reference;
          links leave the SPA for the marketing site (plain <a>). */}
      <footer className={cn(styles['footer'])}>
        <p className={cn(styles['footerCopyright'])}>
          {t('settingsBilling:creditsInfo.footer.copyright', { year })}
          <span className={cn(styles['footerDomain'])}>
            {/* i18n-exempt: bare domain shown as a mono chip, not translatable prose */} · xdocs.app
          </span>
        </p>
        <nav className={cn(styles['footerLinks'])} aria-label={t('settingsBilling:creditsInfo.footer.nav')}>
          <a href="https://xdocs.app/legal/terms" className={cn(styles['footerLink'])}>
            {t('settingsBilling:creditsInfo.footer.terms')}
          </a>
          <a href="https://xdocs.app/legal/privacy" className={cn(styles['footerLink'])}>
            {t('settingsBilling:creditsInfo.footer.privacy')}
          </a>
          <a href="https://xdocs.app/contact" className={cn(styles['footerLink'])}>
            {t('settingsBilling:creditsInfo.footer.contact')}
          </a>
        </nav>
      </footer>
    </div>
  );
}
